/**
 * Wrapper de fetch para o Worker em /api.
 * Usa cookie httpOnly (credentials: include).
 * 401 → redirect /login.
 */

const BASE = "/api";

class ApiError extends Error {
  constructor(public status: number, message: string, public body?: unknown) {
    super(message);
  }
}

/**
 * True quando a API recusou o captcha (`verification_failed`). As telas de auth
 * usam isso pra explicar que o desafio expirou — em vez de acusar credencial
 * errada — e pra remontar o widget, já que o token do Turnstile é de uso único.
 */
export function isCaptchaError(err: unknown): boolean {
  const body = (err as { body?: unknown } | null)?.body;
  return (
    typeof body === "object" &&
    body !== null &&
    (body as { error?: string }).error === "verification_failed"
  );
}

export async function apiFetch<T>(
  path: string,
  init: RequestInit = {}
): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(init.headers ?? {}),
    },
    ...init,
  });

  if (res.status === 401 && !path.startsWith("/auth")) {
    window.location.href = "/login";
    throw new ApiError(401, "unauthorized");
  }

  if (!res.ok) {
    const text = await res.text();
    let body: unknown = text;
    try {
      body = JSON.parse(text);
    } catch {
      // text already
    }
    throw new ApiError(res.status, res.statusText, body);
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/** Streaming SSE para chat (Sofia/Ana). */
export async function* apiStream(
  path: string,
  body: unknown,
  signal?: AbortSignal
): AsyncGenerator<string, void, void> {
  const res = await fetch(`${BASE}${path}`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    signal,
  });

  if (!res.ok || !res.body) {
    throw new ApiError(res.status, res.statusText);
  }

  const reader = res.body.pipeThrough(new TextDecoderStream()).getReader();
  let buf = "";
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += value;
    const lines = buf.split("\n");
    buf = lines.pop() ?? "";
    for (const line of lines) {
      if (line.startsWith("data: ")) {
        const data = line.slice(6);
        if (data === "[DONE]") return;
        yield data;
      }
    }
  }
}

export { ApiError };
